import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";

interface DisclaimerModalProps {
  open: boolean;
  onAccept: () => void;
}

export function DisclaimerModal({ open, onAccept }: DisclaimerModalProps) {
  const [accepted, setAccepted] = React.useState(false);

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  return (
    <Dialog 
      open={open} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown
      aria-labelledby="disclaimer-dialog-title"
    >
      <DialogTitle 
        id="disclaimer-dialog-title" 
        sx={{ 
          backgroundColor: "#f5f5f5", 
          borderBottom: "3px solid #d32f2f",
          color: "#d32f2f",
          fontWeight: 600
        }}
      >
        Important Legal Disclaimer
      </DialogTitle>
      
      <DialogContent sx={{ p: 4 }}>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: "#444" }}>
          <strong>Fi Sci Pty Ltd</strong> provides this platform for informational and planning purposes only. 
          By using this platform, you acknowledge and agree that:
        </Typography>
        
        <Box component="ul" sx={{ pl: 3, mb: 3, "& li": { mb: 2, lineHeight: 1.8, color: "#444" } }}>
          <li>
            <strong>No Liability:</strong> Fi Sci Pty Ltd accepts no responsibility or liability for any losses, 
            damages, injuries, or adverse outcomes arising from fire, natural disasters, or any other events, 
            whether or not related to the use of this platform.
          </li>
          <li>
            <strong>User Responsibility:</strong> It is entirely your responsibility to verify, validate, 
            and assess the robustness of any plans, simulations, or recommendations generated by this platform 
            before implementation.
          </li>
          <li>
            <strong>Professional Consultation:</strong> You should consult with qualified professionals, 
            local authorities, and experts before making any decisions based on information from this platform.
          </li>
          <li>
            <strong>No Warranty:</strong> This platform and its outputs are provided "as is" without any 
            warranties, express or implied, regarding accuracy, completeness, or fitness for any particular purpose.
          </li>
        </Box>
        
        <Box 
          sx={{ 
            p: 3,
            backgroundColor: "#fff3e0",
            border: "2px solid #ff9800",
            borderRadius: 2,
            mb: 3
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 600, 
              color: "#e65100", 
              textAlign: "center"
            }}
          >
            Use of this platform constitutes acceptance of these terms and conditions.
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
          For full product details, please refer to the{" "}
          <Link
            href="https://www.fi-sci-mitigate.com/mitigate-product-description-1"
            target="_blank"
            rel="noopener noreferrer"
          >
            Product Description
          </Link>
          .
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              I have read, understood, and agree to the above terms and disclaimer
            </Typography>
          }
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, backgroundColor: "#f5f5f5" }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          disabled={!accepted}
          sx={{ 
            backgroundColor: "#00d4ad",
            "&:hover": { backgroundColor: "#00b894" },
            minWidth: 120,
            fontWeight: 600
          }}
        >
          Accept & Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}
